import type {
  CollectionStats,
  CreateCollectionRequest,
  CreateCollectionResponse,
  DocumentResponse,
  DownloadUrlResponse,
  PresignConfirmResponse,
  PresignRequestBody,
  PresignUploadResponse,
} from '@/features/collections/types';

export class RagApiError extends Error {
  readonly status: number;
  constructor(status: number, message: string) {
    super(message);
    this.name = 'RagApiError';
    this.status = status;
  }
}

// Headers que o browser proíbe setar manualmente via XHR/fetch (forbidden
// header names da spec) — o navegador os define sozinho a partir do body/
// conexão real. O rag-service assina Content-Length no presign, mas como o
// PUT sempre manda o arquivo inteiro como body, o valor que o browser calcula
// bate com o assinado (ambos vêm de `file.size`) — não precisa (e não pode)
// ser setado explicitamente aqui.
const FORBIDDEN_PUT_HEADERS = new Set(['content-length', 'host']);

async function request<T>(url: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      // JWT de sessão emitido pelo backend após login no MT LOGIN — o
      // rag-service valida assinatura/iss/aud/exp (ADR-050 do rag-pipeline).
      Authorization: `Bearer ${token}`,
      ...init?.headers,
    },
  });
  if (!res.ok) {
    let detail = res.statusText;
    try {
      const body = (await res.json()) as { detail?: string };
      if (body.detail) detail = body.detail;
    } catch {
      /* ignore */
    }
    throw new RagApiError(res.status, detail);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

export async function listCollections(baseUrl: string, token: string): Promise<CollectionStats[]> {
  return request<CollectionStats[]>(`${baseUrl}/v1/collections`, token);
}

export async function createCollection(
  baseUrl: string,
  token: string,
  body: CreateCollectionRequest,
): Promise<CreateCollectionResponse> {
  return request<CreateCollectionResponse>(`${baseUrl}/v1/collections`, token, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function deleteCollection(
  baseUrl: string,
  token: string,
  collectionId: string,
): Promise<void> {
  return request<void>(`${baseUrl}/v1/collections/${collectionId}`, token, { method: 'DELETE' });
}

export async function listDocuments(
  baseUrl: string,
  token: string,
  collectionId: string,
): Promise<DocumentResponse[]> {
  return request<DocumentResponse[]>(`${baseUrl}/v1/collections/${collectionId}/documents`, token);
}

export async function deleteDocument(
  baseUrl: string,
  token: string,
  collectionId: string,
  documentId: string,
): Promise<void> {
  return request<void>(`${baseUrl}/v1/collections/${collectionId}/documents/${documentId}`, token, {
    method: 'DELETE',
  });
}

export async function getDownloadUrl(
  baseUrl: string,
  token: string,
  collectionId: string,
  documentId: string,
): Promise<DownloadUrlResponse> {
  return request<DownloadUrlResponse>(
    `${baseUrl}/v1/collections/${collectionId}/documents/${documentId}/download-url`,
    token,
  );
}

export async function presignUpload(
  baseUrl: string,
  token: string,
  collectionId: string,
  body: PresignRequestBody,
): Promise<PresignUploadResponse> {
  return request<PresignUploadResponse>(
    `${baseUrl}/v1/collections/${collectionId}/documents/presign`,
    token,
    { method: 'POST', body: JSON.stringify(body) },
  );
}

/** PUT direto ao GCS — sem Authorization (a assinatura V4 já autoriza aquele PUT específico). */
export async function putToSignedUrl(
  uploadUrl: string,
  requiredHeaders: Record<string, string>,
  file: File,
  onProgress: (pct: number) => void,
): Promise<void> {
  return new Promise<void>((resolve, reject) => {
    const xhr = new XMLHttpRequest();

    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else {
        reject(new RagApiError(xhr.status, 'Falha ao enviar arquivo pro storage.'));
      }
    };
    xhr.onerror = () => reject(new RagApiError(0, 'Falha de rede no upload.'));

    xhr.open('PUT', uploadUrl);
    for (const [key, value] of Object.entries(requiredHeaders)) {
      if (!FORBIDDEN_PUT_HEADERS.has(key.toLowerCase())) xhr.setRequestHeader(key, value);
    }
    xhr.send(file);
  });
}

export async function confirmUpload(
  baseUrl: string,
  token: string,
  collectionId: string,
  documentId: string,
): Promise<PresignConfirmResponse> {
  return request<PresignConfirmResponse>(
    `${baseUrl}/v1/collections/${collectionId}/documents/${documentId}:confirm`,
    token,
    { method: 'POST' },
  );
}
